import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CivilDate } from 'tc39-proposal-temporal';
import _ from 'underscore';
import { compareCivilMonth, isCivilMonthInRange } from '@bingads-webui-universal/temporal-utitlities';
import { WithArrowKeys } from '@bingads-webui-react/hoc-with-arrow-keys';
import { keyCode } from '@bingads-webui/keycode';
import { submitEvent } from '@bingads-webui-universal/accessibility-helper';

import { MonthsGroup, MonthsRowCount } from './constants';
import { MonthItem } from './month-item';
import { CalendarYear } from './calendar-year';
import './month-calender.less';

const formatMonth = (monthIndex, i18n) => i18n.getMonth(monthIndex + 1, { style: 'abbreviated' });
export const generateKey = (row, column) => (row * MonthsRowCount) + column;

class CalendarMonthInternal extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      navigatedYear: props.isMonthRangePicker
        ? props.navigatedDate.year
        : props.selectedDate.year,
      showYearPicker: false,
      focusedMonth: 0,
    };

    const {
      observable,
    } = props;

    let targetDate = null;
    const observer = {
      next: (data) => {
        if (this.state.showYearPicker) {
          return;
        }

        switch (data) {
          case keyCode.ArrowUp:
            targetDate = this.addMonth(this.state.focusedMonth, -1 * MonthsRowCount);
            this.setState({
              focusedMonth: targetDate,
            });
            break;
          case keyCode.ArrowDown:
            targetDate = this.addMonth(this.state.focusedMonth, MonthsRowCount);
            this.setState({
              focusedMonth: targetDate,
            });
            break;
          case keyCode.ArrowLeft:
            targetDate = this.addMonth(this.state.focusedMonth, -1);
            this.setState({
              focusedMonth: targetDate,
            });
            break;
          case keyCode.ArrowRight:
            targetDate = this.addMonth(this.state.focusedMonth, 1);
            this.setState({
              focusedMonth: targetDate,
            });
            break;
          default:
            break;
        }
      },
    };

    observable.subscribe(observer);
  }

  onSelectYear = (year) => {
    this.setState({
      showYearPicker: false,
    });
    this.navigateYear(year);
  }

  getMonths = (year) => {
    const { selectedDate } = this.props;
    const [selectedRange] = this.props.selectedRanges.length > 0
      ? this.props.selectedRanges
      : this.props.defaultSelectedRanges;
    const {
      minDate,
      maxDate,
    } = this.props;

    let date = new CivilDate(year, 0, 1);

    return MonthsGroup.map(monthRows => monthRows.map((month) => {
      date = date.plus({ months: 1 });
      return {
        key: month,
        isSelected: selectedDate
          ? compareCivilMonth(date, selectedDate) === 0
          : false,
        isInSelectedRange: selectedRange && selectedRange.start && selectedRange.end
          && isCivilMonthInRange(date, selectedRange),
        isSelectedRangeStartOrEnd: selectedRange && selectedRange.start && selectedRange.end
          && _.some(selectedRange, boundary =>
            compareCivilMonth(date, boundary) === 0),
        isInBounds: (!minDate || compareCivilMonth(minDate, date) <= 0) &&
          (!maxDate || compareCivilMonth(date, maxDate) <= 0),
      };
    }));
  }

  getNavigateYear = () => (this.props.isMonthRangePicker
    ? this.props.navigatedDate.year
    : this.state.navigatedYear);

  getMonthItemClasses = (month) => {
    if (!this.props.isMonthRangePicker) { return null; }

    return classNames({
      isSelected: month.isSelected,
      isInSelectedRange: month.isInSelectedRange,
      isSelectedRangeStartOrEnd: month.isSelectedRangeStartOrEnd,
      isInBounds: month.isInBounds,
    });
  }

  addMonth = (date, month) => {
    if (month + date > 12 || month + date < 0) {
      return this.state.focusedMonth;
    }
    return date + month;
  }

  navigateYear = (nextYear) => {
    if (this.props.isMonthRangePicker) {
      if (this.props.onNavigateDate) {
        this.props.onNavigateDate(new CivilDate(nextYear, 1, 1), false);
      }
    } else {
      this.setState({
        navigatedYear: nextYear,
      });
    }
  }

  navigateToNextYear = () => {
    if (this.canNavigateToNextYear()) {
      this.navigateYear(this.getNavigateYear() + 1);
    }
  }

  navigateToPrevYear = () => {
    if (this.canNavigateToPrevYear()) {
      this.navigateYear(this.getNavigateYear() - 1);
    }
  }

  canNavigateToPrevYear = () => {
    const { minDate } = this.props;
    return !minDate || minDate.year < this.getNavigateYear();
  }

  canNavigateToNextYear = () => {
    const { maxDate } = this.props;
    return !maxDate || maxDate.year > this.getNavigateYear();
  }

  handleYearPickClick = () => {
    this.setState({
      showYearPicker: true,
    });
  }

  focusItemCallback = (monthIdx) => {
    this.setState({
      focusedMonth: monthIdx,
    });
  }

  render() {
    const {
      i18n,
      minDate,
      maxDate,
      isMonthRangePicker,
      isLeftNavigateVisible,
      isRightNavigateVisible,
      onSelectMonth,
    } = this.props;

    const navigateYear = this.getNavigateYear();
    const months = this.getMonths(navigateYear);

    return (
      this.state.showYearPicker
        ? <CalendarYear
          i18n={i18n}
          onSelectYear={this.onSelectYear}
          year={this.getNavigateYear()}
          minDate={minDate}
          maxDate={maxDate}
        />
        : (
          <div className={classNames('calendar-month', { 'calendar-month-range-picker': isMonthRangePicker })}>
            <div className="header">
              <div className="year-components">
                <div className="navContainer">
                  {
                    isLeftNavigateVisible && (
                      <div
                        className="prevMonth"
                        tabIndex="0"
                        role="button"
                        onClick={this.navigateToPrevYear}
                        onKeyDown={this.canNavigateToPrevYear() ?
                          event => submitEvent(this.navigateToPrevYear)({ keyCode: event.which })
                          : undefined}
                      >
                        <span className={classNames([this.canNavigateToPrevYear() ? '' : 'disabled', 'glyphicon', 'glyphicon-menu-left'])} />
                      </div>
                    )
                  }
                  <div className="monthAndYear">
                    {
                      isMonthRangePicker ? (
                        <div>{navigateYear}</div>
                      ) : (
                        <div
                          role="button"
                          tabIndex="0"
                          className="monthYearButton"
                          onClick={this.handleYearPickClick}
                          onKeyDown={
                            event => submitEvent(this.handleYearPickClick)({ keyCode: event.which })
                          }
                        >
                          {navigateYear}
                        </div>
                      )
                    }
                  </div>
                  {
                    isRightNavigateVisible && (
                      <div
                        className="nextMonth"
                        tabIndex="0"
                        role="button"
                        onClick={this.navigateToNextYear}
                        onKeyDown={this.canNavigateToNextYear() ?
                          event => submitEvent(this.navigateToNextYear)({ keyCode: event.which })
                          : undefined}
                      >
                        <span className={classNames([this.canNavigateToNextYear() ? '' : 'disabled', 'glyphicon', 'glyphicon-menu-right'])} />
                      </div>
                    )
                  }
                </div>
              </div>
              <div>
                <table className="table">
                  <tbody>
                    {
                      _.map(months, (month, index) => (
                        <tr
                          className="row"
                          key={index}
                        >
                          {
                            _.map(month, (monthItem, monthIndex) => {
                              const idx = index;
                              return (
                                <MonthItem
                                  month={monthItem}
                                  key={generateKey(idx, monthIndex)}
                                  monthIdx={generateKey(idx, monthIndex)}
                                  year={navigateYear}
                                  onSelectMonth={onSelectMonth}
                                  formatMonth={formatMonth}
                                  i18n={i18n}
                                  monthClass={this.getMonthItemClasses(monthItem)}
                                  isFocused={
                                    (idx * MonthsRowCount) + monthIndex === this.state.focusedMonth
                                  }
                                  focusItemCallback={this.focusItemCallback}
                                  isMonthRangePicker={isMonthRangePicker}
                                />
                              );
                            })
                          }

                        </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ));
  }
}

CalendarMonthInternal.propTypes = {
  i18n: PropTypes.shape({
    getToday: PropTypes.func.isRequired,
  }).isRequired,
  navigatedDate: PropTypes.instanceOf(CivilDate),
  onSelectMonth: PropTypes.func.isRequired,
  observable: PropTypes.shape({
    subscribe: PropTypes.func.isRequired,
  }).isRequired,
  selectedDate: PropTypes.instanceOf(CivilDate),
  minDate: PropTypes.instanceOf(CivilDate).isRequired,
  maxDate: PropTypes.instanceOf(CivilDate).isRequired,
  isMonthRangePicker: PropTypes.bool,
  defaultSelectedRanges: PropTypes.arrayOf(PropTypes.shape({
    start: PropTypes.instanceOf(CivilDate),
    end: PropTypes.instanceOf(CivilDate),
  })),
  selectedRanges: PropTypes.arrayOf(PropTypes.shape({
    start: PropTypes.instanceOf(CivilDate),
    end: PropTypes.instanceOf(CivilDate),
  })),
  isLeftNavigateVisible: PropTypes.bool,
  isRightNavigateVisible: PropTypes.bool,
  onNavigateDate: PropTypes.func,
};

CalendarMonthInternal.defaultProps = {
  navigatedDate: undefined,
  isMonthRangePicker: false,
  selectedDate: undefined,
  defaultSelectedRanges: [],
  selectedRanges: [],
  isLeftNavigateVisible: true,
  isRightNavigateVisible: true,
  onNavigateDate: undefined,
};

export const CalendarMonth = WithArrowKeys(CalendarMonthInternal);
